<template>
  <div class="app-container">
    <Splash v-if="isLoading" />

    <div
      v-else
      class="app-container"
      :class="{
        'app-container--public': isPublicLayout,
        'app-container--public-restricted': isPublicContentRestricted,
        'app-container--app': !isPublicLayout,
      }"
      @contextmenu="handleRestrictedContextMenu"
      @dragstart="handleRestrictedDragStart"
      @copy="handleRestrictedCopy"
      @cut="handleRestrictedCopy"
    >
      <PublicTopBar v-if="showPublicTopBar" />
      <TopBar v-else-if="showAppTopBar" />

      <div class="content-area" :class="{ 'content-area--public': isPublicLayout }">
        <router-view />
      </div>

      <global-error-handler />
    </div>
  </div>
</template>

<script>
import TopBar from './common/components/layout/TopBar.vue';
import PublicTopBar from './common/components/layout/PublicTopBar.vue';
import Splash from './core/components/Splash.vue';
import GlobalErrorHandler from './common/components/GlobalErrorHandler.vue';
import { mapState, mapActions, mapGetters } from 'vuex';
import { setCookie, getCookie } from '@/utils/cookies';
import PresenceManager from "@/services/presence.manager";
import authUserService from "@/services/auth-user.service";

export default {
  name: 'App',
  components: {
    TopBar,
    PublicTopBar,
    Splash,
    GlobalErrorHandler,
  },
  data() {
    return {
      isLoading: true,
      presenceStarted: false,
      onBeforeUnloadHandler: null,
      presenceStartFailed: false,
      authExpiryTimer: null,
      authExpiryInProgress: false,
      authExpiredHandler: null,
    };
  },
  computed: {
    isPublicLayout() {
      return this.$route.meta?.layout === "public";
    },

    isPublicContentRestricted() {
      return this.isPublicLayout && this.$route.name !== "login";
    },

    showPublicTopBar() {
      return this.isPublicLayout && this.$route.meta?.hideTopbar !== true;
    },

    showAppTopBar() {
      return !this.isPublicLayout && this.$route.meta?.hideTopbar !== true;
    },

    ...mapGetters('auth', ['isAuthenticated']),
    ...mapState({}),
  },
  methods: {
    ...mapActions('auth', ['initAuth']),

    handleRestrictedContextMenu(event) {
      if (this.isPublicContentRestricted && !this.isFormControl(event.target)) {
        event.preventDefault();
      }
    },

    handleRestrictedDragStart(event) {
      if (this.isPublicContentRestricted && event.target?.tagName === "IMG") {
        event.preventDefault();
      }
    },

    handleRestrictedCopy(event) {
      if (this.isPublicContentRestricted && !this.isFormControl(event.target)) {
        event.preventDefault();
      }
    },

    isFormControl(target) {
      return Boolean(target?.closest?.("input, textarea, select, [contenteditable='true']"));
    },

    getCurrentProjectId() {
      const p = this.$route?.params?.programmeId ?? this.$route?.params?.id;
      const n = Number(p);
      return Number.isFinite(n) ? n : null;
    },

    getCurrentRoute() {
      return this.$route?.fullPath ?? null;
    },

    async ensurePresenceStarted() {
      if (this.presenceStarted) return;
      if (this.presenceStartFailed) return;
      if (!this.isAuthenticated) return;

      try {
        const started = await PresenceManager.start({
          intervalMs: 60_000,
          getCurrentProjectId: () => this.getCurrentProjectId(),
          getCurrentRoute: () => this.getCurrentRoute(),
        });
        this.presenceStarted = !!started;
        this.presenceStartFailed = false;
      } catch (e) {
        this.presenceStartFailed = true;
        console.warn("Presence start failed", e);
      }
    },

    async stopPresence() {
      try {
        await PresenceManager.stop();
      } catch {
        // best-effort
      } finally {
        this.presenceStarted = false;
      }
    },

    clearAuthExpiryTimer() {
      if (this.authExpiryTimer) {
        window.clearTimeout(this.authExpiryTimer);
        this.authExpiryTimer = null;
      }
    },

    async expireAuthSession({ skipServer = false } = {}) {
      if (this.authExpiryInProgress || !this.isAuthenticated) return;
      this.authExpiryInProgress = true;
      this.clearAuthExpiryTimer();

      const redirect = this.$route?.fullPath;
      await this.$store.dispatch('auth/logout', {
        skipServer,
        redirect: false,
      });
      await this.$router.replace({
        name: 'login',
        query: { reason: 'session-expired', ...(redirect ? { redirect } : {}) },
      });
      this.authExpiryInProgress = false;
    },

    scheduleAuthExpiry() {
      this.clearAuthExpiryTimer();
      if (!this.isAuthenticated) return;

      const remainingMs = authUserService.startSessionWindow();
      if (remainingMs <= 0) {
        this.expireAuthSession();
        return;
      }

      this.authExpiryTimer = window.setTimeout(() => {
        this.expireAuthSession();
      }, remainingMs);
    },

    handleAuthExpired() {
      // The API has already rejected the request. Do not make a second
      // authenticated/CSRF-protected request while trying to recover from a
      // 401; that request can be the thing that keeps the UI waiting.
      this.expireAuthSession({ skipServer: true });
    },

    handlePageRefresh() {
      const path = this.$route?.path ?? '/';

      if (path === '/') {
        const lastPath = this.getLastPathForTab();
        const isRefresh = this.isPageRefresh();

        if (isRefresh && lastPath && lastPath !== '/') {
          this.$router.push(lastPath);
          return true;
        }

        this.$router.push('/programmes/');
        return true;
      }

      return false;
    },

    getLastPathForTab() {
      const tabId = this.getTabId();
      let tabPaths = {};

      try {
        tabPaths = JSON.parse(getCookie('tabPaths') || '{}');
      } catch {
        tabPaths = {};
      }

      const tabPath = tabPaths[tabId];
      const globalPath = getCookie('lastPath');

      return tabPath || globalPath;
    },

    saveLastPathForTab(path) {
      const tabId = this.getTabId();
      let tabPaths = {};

      try {
        tabPaths = JSON.parse(getCookie('tabPaths') || '{}');
      } catch {
        tabPaths = {};
      }

      tabPaths[tabId] = path;
      setCookie('tabPaths', JSON.stringify(tabPaths), 7);
      setCookie('lastPath', path, 7);
    },

    isPageRefresh() {
      const navigationEntries = performance.getEntriesByType('navigation');

      if (navigationEntries.length > 0) {
        return navigationEntries[0].type === 'reload';
      }

      if (performance.navigation) {
        return performance.navigation.type === performance.navigation.TYPE_RELOAD;
      }

      return false;
    },

    getTabId() {
      let tabId = sessionStorage.getItem('tabId');

      if (!tabId) {
        tabId = Date.now().toString(36) + Math.random().toString(36).substr(2);
        sessionStorage.setItem('tabId', tabId);
      }

      window.tabId = tabId;
      return tabId;
    },
  },
  watch: {
    async isLoading(newValue) {
      if (!newValue) {
        if (this.isAuthenticated) {
          this.handlePageRefresh();
        }

        await this.ensurePresenceStarted();
      }
    },

    async isAuthenticated(newVal) {
      if (newVal) {
        await this.ensurePresenceStarted();
        this.scheduleAuthExpiry();
      } else {
        await this.stopPresence();
        this.clearAuthExpiryTimer();
      }
    },

    async '$route'(to) {
      if (this.isAuthenticated && to.path !== '/') {
        this.saveLastPathForTab(to.path);
      }

      if (this.isAuthenticated) {
        await this.ensurePresenceStarted();
        await PresenceManager.heartbeatNow({
          getCurrentProjectId: () => this.getCurrentProjectId(),
          getCurrentRoute: () => this.getCurrentRoute(),
        });
      }
    },
  },
  async mounted() {
    try {
      this.authExpiredHandler = () => this.handleAuthExpired();
      window.addEventListener('scio:auth-expired', this.authExpiredHandler);

      this.onBeforeUnloadHandler = () => {
        sessionStorage.setItem('windowClosing', 'true');
        PresenceManager.endSessionOnPageExit();
      };

      window.addEventListener('beforeunload', this.onBeforeUnloadHandler);
      await this.initAuth();
      this.scheduleAuthExpiry();
    } catch (error) {
      console.error('Authentication init error:', error);
    } finally {
      await this.ensurePresenceStarted();
      this.isLoading = false;
    }
  },

  beforeUnmount() {
    if (this.authExpiredHandler) {
      window.removeEventListener('scio:auth-expired', this.authExpiredHandler);
    }
    this.clearAuthExpiryTimer();

    if (this.onBeforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.onBeforeUnloadHandler);
    }

    this.stopPresence();
  },
};
</script>

<style>
:root {
  --topbar-height: 56px;
}

html,
body,
#app {
  height: 100%;
  margin: 0;
  overflow: hidden;
}

.app-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 0;
  height: 100dvh;
  overflow: hidden;
}


.app-container--public {
  background:
    radial-gradient(ellipse at center, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.4) 60%, rgba(0, 0, 0, 0.9) 100%),
    #0a0a0a;
}

.app-container--app {
  background-color: #f8f8f8;
}

.content-area {
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  background-color: var(--bg-scio-grey-n0, #f8f8f8);
  scrollbar-gutter: stable;
}

.content-area:has(.scio-se-spatial-page) {
  overflow: hidden;
  scrollbar-gutter: auto;
}


.content-area--public {
  background: transparent;
}

.content-area.scio-snap-page {
  scroll-snap-type: y mandatory;
  scroll-behavior: smooth;
  overscroll-behavior-y: contain;
}

.content-area.scio-snap-page .scio-snap-section {
  scroll-snap-align: start;
  scroll-snap-stop: always;
}

@media (prefers-reduced-motion: reduce) {
  .content-area.scio-snap-page {
    scroll-behavior: auto;
  }
}

.app-container--public-restricted {
  user-select: none;
}

.app-container--public-restricted img {
  -webkit-user-drag: none;
  user-drag: none;
}

.app-container--public-restricted input,
.app-container--public-restricted textarea,
.app-container--public-restricted select,
.app-container--public-restricted [contenteditable="true"] {
  user-select: text;
}
</style>
